/**
 * Colaboradores (Tejedores) — VIDA-297, fase F3.
 *
 * Una sola sección con dos pestañas, como acordamos:
 *  - «Colaboradores»: las fichas de la RELACIÓN de esta organización con cada
 *    tejedor (CollaboratorLink). Se pueblan solas al aprobar una solicitud.
 *  - «Solicitudes»: la bandeja de altas (VIDA-307), embebida como pestaña con
 *    contador de pendientes.
 *
 * Permisos independientes: `collaborators:read/manage` para las fichas,
 * `weavers:read/manage` para las solicitudes. Cada pestaña se muestra solo si
 * el usuario tiene el permiso correspondiente.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PencilSquareIcon,
  CheckIcon,
  TrashIcon,
  UsersIcon,
  UserPlusIcon,
  InboxArrowDownIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';
import { collaboratorsApi, type CollaboratorLink, type CollaboratorTaxonomyNode } from '@/services/api';
import { LoadingSpinner } from '@/components/common';
import { WeaverApplicationsInbox } from './Weavers';

interface Taxonomy {
  categorias: CollaboratorTaxonomyNode[];
  distintivos: CollaboratorTaxonomyNode[];
}
const EMPTY_TAXONOMY: Taxonomy = { categorias: [], distintivos: [] };

// Selector de chips multi-selección. Si los nodos tienen hijos (categorías,
// 2 niveles) los agrupa bajo el nombre del padre; si son planos (distintivos)
// los pinta seguidos.
function TaxonomyPicker({
  nodes,
  selected,
  onChange,
}: {
  nodes: CollaboratorTaxonomyNode[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  if (nodes.length === 0) {
    return <p className="text-xs text-gray-400">No hay opciones definidas todavía.</p>;
  }
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const chip = (n: CollaboratorTaxonomyNode) => (
    <button
      key={n.id}
      type="button"
      onClick={() => toggle(n.id)}
      className={`px-2 py-1 rounded-full text-xs border transition-colors ${
        selected.includes(n.id)
          ? 'bg-vida-600 text-white border-vida-600'
          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {n.name}
    </button>
  );

  const parents = nodes.filter((n) => n.depth === 0);
  const childrenOf = (pid: string) => nodes.filter((n) => n.parent === pid);
  const hasGroups = parents.some((p) => childrenOf(p.id).length > 0);

  if (!hasGroups) {
    return <div className="flex flex-wrap gap-1.5">{nodes.map(chip)}</div>;
  }
  return (
    <div className="space-y-2">
      {parents.map((p) => {
        const kids = childrenOf(p.id);
        const opts = kids.length ? kids : [p];
        return (
          <div key={p.id}>
            <p className="text-xs font-medium text-gray-500 mb-1">{p.name}</p>
            <div className="flex flex-wrap gap-1.5">{opts.map(chip)}</div>
          </div>
        );
      })}
    </div>
  );
}

type LinkStatus = CollaboratorLink['status'];

const STATUS_LABEL: Record<LinkStatus, string> = {
  invited: 'Invitado',
  active: 'Activo',
  paused: 'En pausa',
  ended: 'Finalizado',
};

const STATUS_COLOR: Record<LinkStatus, string> = {
  invited: 'bg-white border border-gray-300 text-gray-600',
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  ended: 'bg-gray-100 text-gray-500',
};

function formatDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Productos que el tejedor ofrece a la red
// ---------------------------------------------------------------------------

interface SharedProduct {
  _id: string;
  name?: string;
  slug: string;
  status?: string;
  price?: number;
  sku?: string;
  mainImage?: string;
}

function SharedProducts({ linkId }: { linkId: string }) {
  const [products, setProducts] = useState<SharedProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    collaboratorsApi
      .products(linkId)
      .then((res) => { if (alive) setProducts(res.data.data || []); })
      .catch(() => { if (alive) setProducts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [linkId]);

  if (loading) return <div className="py-6 flex justify-center"><LoadingSpinner /></div>;
  if (products.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-4">
        Este tejedor todavía no ofrece ningún producto a la red.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {products.map((p) => (
        <div key={p._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate">{p.name || p.slug}</p>
            <p className="text-xs text-gray-500">{p.sku || p.slug}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {p.status && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{p.status}</span>
            )}
            {typeof p.price === 'number' && (
              <span className="font-medium text-gray-900">
                {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(p.price)}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel de detalle / edición de una ficha
// ---------------------------------------------------------------------------

function LinkDetailPanel({
  link,
  canManage,
  taxonomy,
  onClose,
  onSaved,
  onDeleted,
}: {
  link: CollaboratorLink;
  canManage: boolean;
  taxonomy: Taxonomy;
  onClose: () => void;
  onSaved: (updated: CollaboratorLink) => void;
  onDeleted: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    status: link.status as LinkStatus,
    alias: link.alias || '',
    since: link.since ? link.since.slice(0, 10) : '',
    contact_name: link.contact_name || '',
    contact_email: link.contact_email || '',
    contact_phone: link.contact_phone || '',
    commission_pct: link.commission_pct ?? '',
    categorias: link.categorias || [],
    distintivos: link.distintivos || [],
    tags: (link.tags || []).join(', '),
    notes: link.notes || '',
  });

  // Nombre por slug, para pintar las categorías/distintivos elegidos en lectura.
  const nombrePorId = new Map(
    [...taxonomy.categorias, ...taxonomy.distintivos].map((n) => [n.id, n.name])
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload: Partial<CollaboratorLink> = {
        status: form.status,
        alias: form.alias.trim(),
        since: form.since ? new Date(form.since).toISOString() : undefined,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        commission_pct: form.commission_pct === '' ? undefined : Number(form.commission_pct),
        categorias: form.categorias,
        distintivos: form.distintivos,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: form.notes.trim(),
      };
      const res = await collaboratorsApi.update(link._id, payload);
      onSaved(res.data.data);
      setEditing(false);
      toast.success('Ficha actualizada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('¿Retirar la ficha de este colaborador? No borra al tejedor, solo el vínculo con esta organización.')) return;
    try {
      await collaboratorsApi.remove(link._id);
      toast.success('Colaborador retirado');
      onDeleted(link._id);
    } catch {
      toast.error('No se pudo retirar');
    }
  };

  // Invita al responsable del tejedor a su propio panel (envía email). Manual y
  // explícito: se confirma/edita el email antes de mandar nada.
  const invite = async () => {
    const email = window.prompt(
      'Email del responsable del tejedor. Recibirá una invitación para acceder a su propio panel (gestionar sus productos).',
      link.contact_email || ''
    );
    if (email === null) return;
    try {
      const res = await collaboratorsApi.invite(link._id, email.trim() || undefined);
      toast.success(res.data?.message || 'Invitación enviada');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'No se pudo invitar al responsable');
    }
  };

  const weaverName = link.weaver?.name || link.alias || 'Tejedor';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Ficha de colaborador</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Identidad del tejedor (su perfil, que edita él) */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-vida-100 flex items-center justify-center text-vida-600 text-xl font-semibold overflow-hidden">
              {link.weaver?.logo_url
                ? <img src={link.weaver.logo_url} alt="" className="w-full h-full object-cover" />
                : weaverName[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 text-lg truncate">{weaverName}</p>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[link.status]}`}>
                  {STATUS_LABEL[link.status]}
                </span>
                {link.weaver?.slug && <span className="text-xs text-gray-400">{link.weaver.slug}</span>}
              </div>
            </div>
          </div>

          {/* Datos de la relación (los edita esta organización) */}
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.status}
                    onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as LinkStatus }))}
                  >
                    {(Object.keys(STATUS_LABEL) as LinkStatus[]).map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Comisión (%)</label>
                  <input
                    type="number" min={0} max={100}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.commission_pct}
                    onChange={(e) => setForm((f) => ({ ...f, commission_pct: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Alias (cómo lo llamamos)</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.alias}
                  onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Colabora desde</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.since}
                  onChange={(e) => setForm((f) => ({ ...f, since: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contacto</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.contact_name}
                    onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={form.contact_phone}
                    onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email de contacto</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Categorías</label>
                <TaxonomyPicker
                  nodes={taxonomy.categorias}
                  selected={form.categorias}
                  onChange={(ids) => setForm((f) => ({ ...f, categorias: ids }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Distintivos</label>
                <TaxonomyPicker
                  nodes={taxonomy.distintivos}
                  selected={form.distintivos}
                  onChange={(ids) => setForm((f) => ({ ...f, distintivos: ids }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Etiquetas (separadas por comas)</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50"
                >
                  <CheckIcon className="w-4 h-4" />
                  Guardar
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Comisión</p>
                  <p className="font-semibold text-gray-900">
                    {typeof link.commission_pct === 'number' ? `${link.commission_pct}%` : '—'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Colabora desde</p>
                  <p className="font-semibold text-gray-900">{formatDate(link.since)}</p>
                </div>
              </div>
              {(link.contact_name || link.contact_email || link.contact_phone) && (
                <div className="text-sm text-gray-700">
                  {link.contact_name && <p>{link.contact_name}</p>}
                  {link.contact_email && <a href={`mailto:${link.contact_email}`} className="text-vida-600 hover:underline">{link.contact_email}</a>}
                  {link.contact_phone && <p className="text-gray-500">{link.contact_phone}</p>}
                </div>
              )}
              {link.notes && (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-line">
                  {link.notes}
                </div>
              )}
              {(link.categorias?.length > 0 || link.distintivos?.length > 0) && (
                <div className="space-y-2">
                  {link.categorias?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Categorías</p>
                      <div className="flex flex-wrap gap-1.5">
                        {link.categorias.map((c) => (
                          <span key={c} className="px-2 py-0.5 bg-vida-50 text-vida-700 rounded-full text-xs">
                            {nombrePorId.get(c) || c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {link.distintivos?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Distintivos</p>
                      <div className="flex flex-wrap gap-1.5">
                        {link.distintivos.map((d) => (
                          <span key={d} className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs">
                            {nombrePorId.get(d) || d}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {link.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {link.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600">{t}</span>
                  ))}
                </div>
              )}
              {canManage && (
                <div className="flex items-center gap-4 flex-wrap">
                  <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm text-vida-600 hover:text-vida-700">
                    <PencilSquareIcon className="w-4 h-4" />
                    Editar
                  </button>
                  <button onClick={invite} className="flex items-center gap-1.5 text-sm text-vida-600 hover:text-vida-700">
                    <UserPlusIcon className="w-4 h-4" />
                    Invitar al responsable
                  </button>
                  <button onClick={remove} className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700">
                    <TrashIcon className="w-4 h-4" />
                    Retirar
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Productos que el tejedor ofrece a la red */}
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
              <BuildingStorefrontIcon className="w-4 h-4" />
              Productos que ofrece
            </p>
            <SharedProducts linkId={link._id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function CollaboratorsPage() {
  const { hasPermission } = useAuthStore();
  const canReadFichas = hasPermission(PERMISSIONS.COLLABORATORS_READ) || hasPermission('*');
  const canManageFichas = hasPermission(PERMISSIONS.COLLABORATORS_MANAGE) || hasPermission('*');
  const canReadInbox = hasPermission(PERMISSIONS.WEAVERS_READ) || hasPermission('*');

  const [tab, setTab] = useState<'fichas' | 'solicitudes'>(canReadFichas ? 'fichas' : 'solicitudes');
  const [pendientes, setPendientes] = useState(0);

  const [links, setLinks] = useState<CollaboratorLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<CollaboratorLink | null>(null);
  const [taxonomy, setTaxonomy] = useState<Taxonomy>(EMPTY_TAXONOMY);

  const load = useCallback(async () => {
    if (!canReadFichas) return;
    setLoading(true);
    try {
      const res = await collaboratorsApi.list();
      setLinks(res.data.data || []);
    } catch {
      toast.error('Error cargando colaboradores');
    } finally {
      setLoading(false);
    }
  }, [canReadFichas]);

  useEffect(() => {
    if (tab === 'fichas') load();
  }, [tab, load]);

  // Taxonomía (categorías/distintivos) para el selector de la ficha; una vez.
  useEffect(() => {
    if (!canReadFichas) return;
    collaboratorsApi
      .taxonomy()
      .then((res) => setTaxonomy({ categorias: res.data.categorias || [], distintivos: res.data.distintivos || [] }))
      .catch(() => setTaxonomy(EMPTY_TAXONOMY));
  }, [canReadFichas]);

  const onSaved = (updated: CollaboratorLink) => {
    setLinks((prev) => prev.map((l) => (l._id === updated._id ? { ...l, ...updated } : l)));
    setSelected((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
  };
  const onDeleted = (id: string) => {
    setLinks((prev) => prev.filter((l) => l._id !== id));
    setSelected(null);
  };

  if (!canReadFichas && !canReadInbox) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No tienes permiso para ver los colaboradores.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Colaboradores</h1>
        <p className="text-sm text-gray-500 mt-0.5">Tejedores que aportan productos a la red de esta organización</p>
      </div>

      {/* Pestañas: solo las que el usuario puede ver */}
      {canReadFichas && canReadInbox && (
        <div className="flex items-center gap-1 border-b border-gray-200">
          <button
            onClick={() => setTab('fichas')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'fichas' ? 'border-vida-600 text-vida-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <UsersIcon className="w-4 h-4" />
            Colaboradores
          </button>
          <button
            onClick={() => setTab('solicitudes')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === 'solicitudes' ? 'border-vida-600 text-vida-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <InboxArrowDownIcon className="w-4 h-4" />
            Solicitudes
            {pendientes > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-vida-600 text-white">{pendientes}</span>
            )}
          </button>
        </div>
      )}

      {/* Contenido */}
      {tab === 'fichas' && canReadFichas && (
        loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : links.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-400">
            <UsersIcon className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Todavía no hay colaboradores.</p>
            {canReadInbox && (
              <p className="text-xs mt-1">Se dan de alta al aprobar una solicitud en la pestaña «Solicitudes».</p>
            )}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {links.map((l) => {
              const name = l.weaver?.name || l.alias || 'Tejedor';
              return (
                <button
                  key={l._id}
                  onClick={() => setSelected(l)}
                  className="text-left bg-white rounded-xl border border-gray-200 p-4 hover:border-vida-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-vida-100 flex items-center justify-center text-vida-600 font-semibold overflow-hidden flex-shrink-0">
                      {l.weaver?.logo_url
                        ? <img src={l.weaver.logo_url} alt="" className="w-full h-full object-cover" />
                        : name[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[l.status]}`}>
                        {STATUS_LABEL[l.status]}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 flex items-center justify-between">
                    <span>{typeof l.commission_pct === 'number' ? `Comisión ${l.commission_pct}%` : 'Sin comisión'}</span>
                    <span>{l.since ? `desde ${formatDate(l.since)}` : ''}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {tab === 'solicitudes' && canReadInbox && (
        <WeaverApplicationsInbox onPendingChange={setPendientes} />
      )}

      {selected && (
        <LinkDetailPanel
          link={selected}
          canManage={canManageFichas}
          taxonomy={taxonomy}
          onClose={() => setSelected(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
